(function () {
  "use strict";

  const RUNTIME_KEY = "MarineMaxCoveoPageLifecycle";
  const existingRuntime = window[RUNTIME_KEY];
  if (existingRuntime && typeof existingRuntime.create === "function") return;

  const ownersByRoot = new WeakMap();

  function normalizeMode(mode) {
    const normalized = String(mode || "").trim();
    if (!normalized) {
      throw new TypeError("Coveo page lifecycle mode is required");
    }
    return normalized;
  }

  function getRootOwners(root, createIfMissing) {
    if (!root || (typeof root !== "object" && typeof root !== "function")) {
      return null;
    }
    let owners = ownersByRoot.get(root);
    if (!owners && createIfMissing) {
      owners = new Map();
      ownersByRoot.set(root, owners);
    }
    return owners || null;
  }

  function create(root, mode) {
    const normalizedMode = normalizeMode(mode);
    const owners = getRootOwners(root, true);
    if (!owners) {
      throw new TypeError("Coveo page lifecycle root is required");
    }

    const previousOwner = owners.get(normalizedMode);
    if (previousOwner) previousOwner.destroy();

    const abortController = new AbortController();
    const animationFrameIds = new Set();
    const cleanupStack = [];
    const subscriptions = new WeakMap();
    const timeoutIds = new Set();
    let active = true;
    let owner = null;

    function reportCleanupError(error) {
      if (typeof console === "undefined" || typeof console.error !== "function") {
        return;
      }
      console.error("[Coveo Lifecycle] Cleanup failed:", error);
    }

    function runCleanup(cleanup) {
      try {
        cleanup();
      } catch (error) {
        reportCleanupError(error);
      }
    }

    function addCleanup(cleanup) {
      if (typeof cleanup !== "function") return function () {};
      if (!active) {
        runCleanup(cleanup);
        return cleanup;
      }
      cleanupStack.push(cleanup);
      return cleanup;
    }

    function guard(callback) {
      if (typeof callback !== "function") {
        throw new TypeError("Coveo lifecycle guard requires a function");
      }
      return function guardedCoveoPageCallback() {
        if (!active || !isCurrent() || root.isConnected === false) {
          return undefined;
        }
        return callback.apply(this, arguments);
      };
    }

    function listen(target, type, listener, options) {
      if (
        !target ||
        typeof target.addEventListener !== "function" ||
        typeof target.removeEventListener !== "function" ||
        typeof listener !== "function"
      ) {
        return null;
      }
      const guardedListener = guard(listener);
      target.addEventListener(type, guardedListener, options);
      addCleanup(function () {
        target.removeEventListener(type, guardedListener, options);
      });
      return guardedListener;
    }

    function subscribe(source, callback) {
      if (
        !source ||
        typeof source.subscribe !== "function" ||
        typeof callback !== "function"
      ) {
        return null;
      }

      let sourceSubscriptions = subscriptions.get(source);
      if (!sourceSubscriptions) {
        sourceSubscriptions = new Map();
        subscriptions.set(source, sourceSubscriptions);
      }
      if (sourceSubscriptions.has(callback)) {
        return sourceSubscriptions.get(callback).unsubscribe;
      }

      const guardedCallback = guard(callback);
      const rawUnsubscribe = source.subscribe(guardedCallback);
      const disposeSubscription =
        typeof rawUnsubscribe === "function"
          ? rawUnsubscribe
          : typeof rawUnsubscribe?.unsubscribe === "function"
            ? rawUnsubscribe.unsubscribe.bind(rawUnsubscribe)
            : null;
      let subscribed = true;
      const unsubscribe = function () {
        if (!subscribed) return false;
        subscribed = false;
        sourceSubscriptions.delete(callback);
        if (disposeSubscription) disposeSubscription();
        return true;
      };
      sourceSubscriptions.set(callback, { guardedCallback, unsubscribe });
      addCleanup(unsubscribe);
      return unsubscribe;
    }

    function setOwnedValue(target, key, value) {
      if (!target || (typeof target !== "object" && typeof target !== "function")) {
        return value;
      }
      const hadOwnValue = Object.prototype.hasOwnProperty.call(target, key);
      const previousValue = target[key];
      target[key] = value;
      addCleanup(function () {
        if (target[key] !== value) return;
        if (hadOwnValue) {
          target[key] = previousValue;
          return;
        }
        try {
          delete target[key];
        } catch (_) {
          target[key] = undefined;
        }
      });
      return value;
    }

    function claimCurrentValue(target, key, value) {
      if (!target || (typeof target !== "object" && typeof target !== "function")) {
        return value;
      }
      target[key] = value;
      addCleanup(function () {
        if (target[key] !== value) return;
        try {
          delete target[key];
        } catch (_) {
          target[key] = undefined;
        }
      });
      return value;
    }

    function setOwnedTimeout(callback, delay) {
      if (typeof callback !== "function" || !active) return null;
      const args = Array.prototype.slice.call(arguments, 2);
      let completedSynchronously = false;
      let timeoutId = null;
      const run = function () {
        completedSynchronously = true;
        if (timeoutId != null) timeoutIds.delete(timeoutId);
        if (!active) return;
        callback.apply(window, args);
      };
      timeoutId = window.setTimeout(run, delay);
      if (!completedSynchronously) timeoutIds.add(timeoutId);
      return timeoutId;
    }

    function clearOwnedTimeout(timeoutId) {
      if (timeoutId == null) return false;
      window.clearTimeout(timeoutId);
      return timeoutIds.delete(timeoutId);
    }

    function requestOwnedAnimationFrame(callback) {
      if (typeof callback !== "function" || !active) return null;
      let completedSynchronously = false;
      let frameId = null;
      const run = function (timestamp) {
        completedSynchronously = true;
        if (frameId != null) animationFrameIds.delete(frameId);
        if (!active) return;
        callback(timestamp);
      };
      frameId = window.requestAnimationFrame(run);
      if (!completedSynchronously) animationFrameIds.add(frameId);
      return frameId;
    }

    function cancelOwnedAnimationFrame(frameId) {
      if (frameId == null) return false;
      window.cancelAnimationFrame(frameId);
      return animationFrameIds.delete(frameId);
    }

    function trackObserver(observer) {
      if (!observer || typeof observer.disconnect !== "function") return observer;
      addCleanup(function () {
        observer.disconnect();
      });
      return observer;
    }

    function isCurrent() {
      const currentOwners = getRootOwners(root, false);
      return !!currentOwners && currentOwners.get(normalizedMode) === owner;
    }

    function destroy() {
      if (!active) return false;
      active = false;
      abortController.abort();

      timeoutIds.forEach(function (timeoutId) {
        window.clearTimeout(timeoutId);
      });
      timeoutIds.clear();
      animationFrameIds.forEach(function (frameId) {
        window.cancelAnimationFrame(frameId);
      });
      animationFrameIds.clear();

      while (cleanupStack.length) {
        runCleanup(cleanupStack.pop());
      }

      const currentOwners = getRootOwners(root, false);
      if (currentOwners && currentOwners.get(normalizedMode) === owner) {
        currentOwners.delete(normalizedMode);
        if (currentOwners.size === 0) ownersByRoot.delete(root);
      }
      return true;
    }

    owner = Object.freeze({
      addCleanup,
      cancelAnimationFrame: cancelOwnedAnimationFrame,
      claim: claimCurrentValue,
      clearTimeout: clearOwnedTimeout,
      destroy,
      guard,
      isCurrent,
      listen,
      mode: normalizedMode,
      own: setOwnedValue,
      requestAnimationFrame: requestOwnedAnimationFrame,
      root,
      setTimeout: setOwnedTimeout,
      signal: abortController.signal,
      subscribe,
      trackObserver,
      get active() {
        return active;
      },
    });
    owners.set(normalizedMode, owner);
    return owner;
  }

  function get(root, mode) {
    const owners = getRootOwners(root, false);
    if (!owners) return null;
    if (mode == null) return owners.values().next().value || null;
    return owners.get(normalizeMode(mode)) || null;
  }

  function has(root, mode) {
    return !!get(root, mode);
  }

  function destroy(root, mode) {
    const owners = getRootOwners(root, false);
    if (!owners) return 0;
    if (mode != null) {
      const owner = owners.get(normalizeMode(mode));
      return owner && owner.destroy() ? 1 : 0;
    }
    let destroyed = 0;
    Array.from(owners.values()).forEach(function (owner) {
      if (owner.destroy()) destroyed += 1;
    });
    return destroyed;
  }

  const runtime = Object.freeze({ create, destroy, get, has });
  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: false,
    enumerable: false,
    value: runtime,
    writable: false,
  });
})();
